import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export type Database = {
  public: {
    Tables: {
      clients: {
        Row: {
          id: string;
          nom: string;
          matricule_fiscal: string;
          adresse: string;
          telephone: string;
          email: string;
          date_inscription: string;
          statut: 'actif' | 'inactif';
          deleted_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['clients']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['clients']['Insert']>;
      };
      factures: {
        Row: {
          id: string;
          numero: string;
          client_id: string;
          date_emission: string;
          montant_ht: number;
          tva_19: number;
          montant_ttc: number;
          services: string;
          statut_paiement: 'payee' | 'non_payee';
          date_paiement: string | null;
          methode_paiement: string | null;
          devis_id: string | null;
          rebours_ids: string[] | null;
          deleted_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['factures']['Row'], 'id' | 'numero' | 'tva_19' | 'montant_ttc' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['factures']['Insert']>;
      };
      rebours: {
        Row: {
          id: string;
          numero: string;
          client_id: string;
          montant: number;
          raison: string;
          date_creation: string;
          date_limite: string;
          notes: string;
          statut_paiement: 'paye' | 'non_paye';
          date_paiement: string | null;
          methode_paiement: string | null;
          image_url: string | null;
          deleted_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['rebours']['Row'], 'id' | 'numero' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['rebours']['Insert']>;
      };
      devis: {
        Row: {
          id: string;
          numero: string;
          client_id: string;
          date_emission: string;
          date_expiration: string;
          montant_ht: number;
          tva_19: number;
          montant_ttc: number;
          description: string;
          statut: 'en-attente' | 'accepte' | 'rejete' | 'transforme';
          facture_id: string | null;
          statut_paiement: 'non_paye' | 'paye' | 'partiellement';
          date_paiement: string | null;
          methode_paiement: string | null;
          montant_partiel: number | null;
          deleted_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['devis']['Row'], 'id' | 'numero' | 'tva_19' | 'montant_ttc' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['devis']['Insert']>;
      };
      charges: {
        Row: {
          id: string;
          numero: string;
          libelle: string;
          type: 'fixe' | 'variable';
          categorie: string | null;
          montant: number;
          frequence: 'mensuel' | 'trimestriel' | 'annuel' | 'ponctuel';
          date_charge: string;
          date_echeance: string | null;
          statut: 'payee' | 'non_payee';
          notes: string | null;
          deleted_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['charges']['Row'], 'id' | 'numero' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['charges']['Insert']>;
      };
      entreprise_settings: {
        Row: {
          id: string;
          nom: string;
          logo_url: string | null;
          matricule_fiscal: string;
          rib: string;
          adresse: string;
          telephone: string;
          email: string;
          site_web: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['entreprise_settings']['Row'], 'id' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['entreprise_settings']['Insert']>;
      };
      historique_modifications: {
        Row: {
          id: string;
          table_name: string;
          record_id: string;
          action: 'create' | 'update' | 'delete';
          ancien_valeur: Record<string, unknown> | null;
          nouveau_valeur: Record<string, unknown> | null;
          created_at: string;
        };
        Insert: Omit<Database['public']['Tables']['historique_modifications']['Row'], 'id' | 'created_at'>;
        Update: never;
      };
    };
  };
};
